import React, { Fragment } from 'react';

import { ColorButton } from '../../UI';
import { Column } from '../../NewStrategy/StrategyTable';
import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { ProfitLoss } from '../../ProfitLoss';
import { Sort } from '../../NewStrategy/Sort';

import { computePercentage } from '../../../helpers/functions';
import { useSort } from '../../../hooks/useSort';

import { Center, StrategyAssetsGroup, TimeFrame } from '../types';
import { Currency } from '../../../types/common';
import { SortType } from '../../NewStrategy/types';
import { logger } from '../../../helpers/logger';
import { useTranslation } from 'react-i18next';

export const GroupsList = ({
  center,
  columns,
  column = false,
  fitMinWidth = false,
  groups,
  interval,
  symbol,
  strategyValue,
  groupBy,
  widgetWidth,
  ExtraLayer = () => <></>,
  handleSelectStrategy,
  resetCenter,
  setCenter,
}: {
  center: Center;
  columns: SortType[];
  column?: boolean;
  fitMinWidth?: boolean;
  groups: StrategyAssetsGroup[];
  interval: TimeFrame;
  symbol: Currency['symbol'];
  strategyValue: number;
  groupBy:
    | 'STRATEGIES'
    | 'SOURCE'
    | 'INSPIRATION'
    | 'EXCHANGE'
    | 'COUNTRY'
    | 'INDUSTRY'
    | 'CURRENCY';
  widgetWidth: number;
  ExtraLayer?(): JSX.Element;
  handleSelectStrategy(): void;
  resetCenter(): void;
  setCenter: React.Dispatch<React.SetStateAction<Center>>;
}) => {
  const initialFieldSorting = 'ALLOCATION';
  const initialSortingDirection = 'sortDown';

  const { t } = useTranslation();

  const { type, getSortValue, handleSort, direction } = useSort(
    columns,
    initialFieldSorting,
    initialSortingDirection
  );

  logger.log('WidgetWithPie::GroupsList', {
    groupBy,
    initialFieldSorting,
    initialSortingDirection,
  });

  // SORT
  const categories = groups.sort((a, b) => {
    let [arg1, arg2]: (number | string)[] = [a.title, b.title];

    type === 'INVESTED' && ([arg1, arg2] = [a.entryValue, b.entryValue]);
    type === 'TOT VALUE' && ([arg1, arg2] = [a.totalValue, b.totalValue]);
    type === 'VARIATION' &&
      ([arg1, arg2] = [
        computePercentage(a.timeFrames[interval], a.totalValue),
        computePercentage(b.timeFrames[interval], b.totalValue),
      ]);
    type === 'ALLOCATION' &&
      ([arg1, arg2] = [
        computePercentage(strategyValue, a.totalValue),
        computePercentage(strategyValue, b.totalValue),
      ]);

    return getSortValue(arg1, arg2);
  });

  return (
    <div
      className={`
        grid
        grid-cols-${column ? (columns.length === 3 ? 1 : 2) : columns.length}
        gap-0
        ${!column && 'border-t border-grey-5'}
      `}
    >
      <ExtraLayer />
      {/* HEADER */}
      {!column &&
        columns.map((title, i) => (
          <div
            key={'pie-table-' + i}
            className={`
              w-full
              py-2
              pl-5
              border-b
              border-grey-10
              dark:border-grey-70
              ${fitMinWidth ? 'w-fit' : ' min-w-32'}
              ${
                !i &&
                widgetWidth === 2 &&
                'bg-white dark:bg-grey-90 sticky left-0 border-r'
              }
            `}
          >
            <Column
              className={`
              flex
              items-center
              w-full
              h-full
            `}
              element={
                <Sort
                  className={`
                    flex
                    items-center
                    text-sm
                    font-medium
                    w-full
                    h-full
                    uppercase
                  `}
                  columnName={
                    title === '1COLUMN'
                      ? t('widgetWithPie.title_' + groupBy)
                      : title
                  }
                  key={title}
                  type={type}
                  handleSort={handleSort}
                  initialDirection={direction}
                />
              }
              span={1}
            />
          </div>
        ))}

      {/* DATA */}
      {categories.map(
        ({ color, entryValue, title, totalValue, timeFrames }, i) => (
          <Fragment key={'pie-table-' + i}>
            {/* COLOR + NAME */}
            <div
              className={`
                ${fitMinWidth ? 'w-fit' : ' min-w-32'}
                flex
                items-center
                pl-5
                w-full
                sticky
                left-0
                bg-white
                dark:bg-grey-90
                ${column && 'col-span-2 py-3 cursor-pointer'}
                ${
                  center.id === title
                    ? column
                      ? 'border-t border-l border-r'
                      : 'border-l border-b border-t'
                    : !column && 'border-b border-grey-10 dark:border-grey-70'
                }
                ${widgetWidth === 2 && 'border-r'}
              `}
              onMouseEnter={() => setCenter({ id: title, value: totalValue })}
              onMouseLeave={() => resetCenter()}
              onClick={handleSelectStrategy}
            >
              <ColorButton radius={3} color={color!} isReadOnly={true} />
              <div className="pl-3 font-medium capitalize">
                {title.toLocaleLowerCase()}
              </div>
            </div>

            {/* INVESTED */}
            {columns?.includes('INVESTED') && (
              <div
                className={`
                 ${fitMinWidth ? 'w-fit' : ' min-w-32'}
                  pl-6
                  flex
                  items-center
                  w-full
                  ${
                    center.id === title
                      ? column
                        ? 'border-l'
                        : 'border-b border-t'
                      : !column && 'border-b border-grey-10 dark:border-grey-70'
                  }
                `}
                onMouseEnter={() => setCenter({ id: title, value: totalValue })}
                onMouseLeave={() => resetCenter()}
                onClick={handleSelectStrategy}
              >
                <span className="flex flex-col h-full justify-center">
                  {column && (
                    <span className="text-grey-50 font-light">Invested</span>
                  )}
                  <CustomNumber
                    className={'cursor-default w-full'}
                    suffix={symbol}
                    value={entryValue}
                    displayType={'text'}
                  />
                </span>
              </div>
            )}

            {/* TOT VAL */}
            <div
              className={`
                ${fitMinWidth ? 'w-fit' : ' min-w-32'}
                pl-6
                flex
                items-center
                w-full
                ${
                  center.id === title
                    ? column
                      ? columns.length === 3
                        ? 'border-r border-l'
                        : 'border-r'
                      : 'border-b border-t'
                    : !column && 'border-b border-grey-10 dark:border-grey-70'
                }
              `}
              onMouseEnter={() => setCenter({ id: title, value: totalValue })}
              onMouseLeave={() => resetCenter()}
              onClick={handleSelectStrategy}
            >
              <span className="flex flex-col h-full py-3">
                {column && (
                  <span className="text-grey-50 font-light">Total value</span>
                )}
                <ProfitLoss
                  percentage={computePercentage(entryValue, totalValue, 100)}
                  value={totalValue}
                  symbol={symbol}
                  displayType={'text'}
                />
              </span>
            </div>

            {/* ALLOCATION */}
            <div
              className={`
              pl-6
              flex
              items-center
              w-full
              ${fitMinWidth ? 'w-fit' : ' min-w-32'}
              ${
                center.id === title
                  ? column
                    ? 'col-span-2 border-b border-r border-l py-3'
                    : 'border-b border-t border-r'
                  : column
                  ? 'col-span-2 border-b border-grey-5 py-3 dark:border-grey-80'
                  : 'border-b border-grey-10 dark:border-grey-70'
              }
            `}
              onMouseEnter={() => setCenter({ id: title, value: totalValue })}
              onMouseLeave={() => resetCenter()}
              onClick={handleSelectStrategy}
            >
              <span className="flex flex-col">
                {column && (
                  <span className="text-grey-50 font-light">% Invested</span>
                )}
                <CustomNumber
                  suffix="%"
                  value={computePercentage(strategyValue, totalValue)}
                  displayType={'text'}
                />
              </span>
            </div>

            {/* VARIATION */}
            {columns?.includes('VARIATION') && !column && (
              <div
                className={`
                 ${fitMinWidth ? 'w-fit' : ' min-w-32'}
                  pl-6
                  flex
                  items-center
                  w-full
                  ${
                    center.id === title
                      ? 'border-b border-t'
                      : 'border-b border-grey-10 dark:border-grey-70'
                  }
                `}
                onMouseEnter={() => setCenter({ id: title, value: totalValue })}
                onMouseLeave={() => resetCenter()}
                onClick={handleSelectStrategy}
              >
                <ProfitLoss
                  percentage={computePercentage(
                    timeFrames[interval],
                    totalValue,
                    100
                  )}
                  symbol={symbol}
                  displayType={'text'}
                  value={
                    computePercentage(timeFrames[interval], totalValue, 100) !==
                    0
                      ? totalValue - timeFrames[interval]
                      : 0
                  }
                />
              </div>
            )}
          </Fragment>
        )
      )}
    </div>
  );
};
